import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateIT } from "@/lib/formatDate";
import CheckinHeader from "./checkin/CheckinHeader";
import CheckinQuestionsEditor from "./checkin/CheckinQuestionsEditor";
import CheckinTrendChart from "./checkin/CheckinTrendChart";
import CheckinResponsesList from "./checkin/CheckinResponsesList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckinTab({ clientId }) {
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/checkin/config`),
        axios.get(`${API}/clients/${clientId}/checkin/responses?limit=12`),
      ]);
      setConfig(c.data);
      setResponses(r.data || []);
      // Mark all unread responses as seen so the red badge on the client card clears.
      // Best-effort — failures are non-blocking.
      const hasUnseen = (r.data || []).some((x) => x.seen_by_coach === false);
      if (hasUnseen) {
        axios
          .post(`${API}/clients/${clientId}/checkin/responses/mark-seen`)
          .catch((err) => console.warn("[CheckinTab] mark-seen failed", err));
      }
    } catch (err) {
      console.error("[CheckinTab] fetch error", err);
      toast.error("Errore nel caricamento");
    }
  }, [clientId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const persist = async (patch) => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/clients/${clientId}/checkin/config`, patch);
      setConfig(data);
      return data;
    } catch (err) {
      console.error("[CheckinTab] persist error", err);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !config?.enabled;
    await persist({ enabled: next });
    toast.success(next ? "Check-in attivato" : "Check-in disattivato");
  };

  const setDay = async (val) => {
    await persist({ day_of_week: Number(val) });
  };

  const updateQuestion = (idx, patch) => {
    const qs = config.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    setConfig({ ...config, questions: qs });
  };

  const moveQuestion = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= config.questions.length) return;
    const qs = [...config.questions];
    [qs[idx], qs[target]] = [qs[target], qs[idx]];
    setConfig({ ...config, questions: qs.map((q, i) => ({ ...q, order: i })) });
  };

  const addCustomQuestion = () => {
    const customCount = (config?.questions || []).filter((q) => q.key.startsWith("custom_")).length;
    const key = `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const qs = [
      ...(config?.questions || []),
      {
        key,
        label: `Nuova domanda ${customCount + 1}`,
        type: "scale",
        enabled: true,
        order: config?.questions?.length || 0,
        min: 1,
        max: 10,
        placeholder: "",
      },
    ];
    setConfig({ ...config, questions: qs });
  };

  const removeQuestion = (idx) => {
    const qs = config.questions
      .filter((_, i) => i !== idx)
      .map((q, i) => ({ ...q, order: i }));
    setConfig({ ...config, questions: qs });
  };

  const saveQuestions = async () => {
    if (!config) return;
    await persist({ questions: config.questions });
    toast.success("Domande salvate");
  };

  // ============ trend chart data ============
  const trendData = useMemo(() => {
    if (!responses.length) return [];
    return [...responses]
      .sort((a, b) => (a.week_start > b.week_start ? 1 : -1))
      .map((r) => {
        const row = { week: formatDateIT(r.week_start) };
        Object.entries(r.answers || {}).forEach(([k, v]) => {
          if (typeof v === "number") row[k] = v;
        });
        return row;
      });
  }, [responses]);

  const scaleKeys = useMemo(() => {
    const keys = new Set();
    responses.forEach((r) => {
      Object.entries(r.answers || {}).forEach(([k, v]) => {
        if (typeof v === "number") keys.add(k);
      });
    });
    return Array.from(keys);
  }, [responses]);

  const labelFor = useCallback(
    (key) => config?.questions?.find((q) => q.key === key)?.label || key,
    [config]
  );

  if (!config) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="checkin-tab" className="space-y-6">
      <CheckinHeader
        enabled={config.enabled}
        dayOfWeek={config.day_of_week}
        saving={saving}
        onToggle={toggleEnabled}
        onChangeDay={setDay}
      />

      <CheckinQuestionsEditor
        questions={config.questions}
        saving={saving}
        onUpdateQuestion={updateQuestion}
        onMoveQuestion={moveQuestion}
        onRemoveQuestion={removeQuestion}
        onAddQuestion={addCustomQuestion}
        onSave={saveQuestions}
      />

      <CheckinTrendChart trendData={trendData} scaleKeys={scaleKeys} labelFor={labelFor} />

      <CheckinResponsesList responses={responses} labelFor={labelFor} />
    </div>
  );
}
